import React, { useState } from "react";
import { Badge, Form, OverlayTrigger, Tooltip, Spinner } from "react-bootstrap";
import { FaPen, FaCheck, FaTimes, FaEllipsisV } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import UserInitialsAvatar from "../../../../utils/userInitialAvatar";
import { getFullName } from "../../../../utils/helpers";
import { updateAllocation, fetchTimeDistributionData } from "../../../../redux/slices/timeDistributionSlice";
import UserAllocationModal from "../users/UserAllocationModal";

const ResourceRow = ({ resource, projectName, workPackages = [], month }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(); 

  const hasConflict = resource.has_conflict;

  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(resource.allocated);
  const [isSaving, setIsSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const hasWorkPackages = workPackages && workPackages.length > 0;
  const userWPs = workPackages.filter(wp => 
    wp.members?.some(m => String(m.user_id) === String(resource.id))
  );

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const parsedValue = parseFloat(value);
      await dispatch(updateAllocation({
        allocationId: resource.allocation_id,
        data: { monthly_etis: parsedValue, month }
      })).unwrap();

      dispatch(fetchTimeDistributionData({ month }));
      setIsEditing(false);

    } catch (err) {
      console.error(t('components.planningTab.errors.save_resource_eti'), err);
      setValue(resource.allocated);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setValue(resource.allocated);
    setIsEditing(false);
  };

  return (
    <>
      <tr className={hasConflict ? "table-danger bg-opacity-10" : ""}>
        <td className="ps-5 py-2">
          <div className="d-flex align-items-center gap-3">
            <UserInitialsAvatar 
              first_name={resource.first_name || ""} 
              last_name={resource.last_name || ""} 
              style={{ width: "28px", height: "28px", fontSize: "0.75rem", flexShrink: 0 }} 
            />
            <div>
              <div className={`fw-semibold ${hasConflict ? "txt-danger" : "txt-main"}`}>
                {getFullName(resource)}
              </div>
              <div className="txt-subtle" style={{ fontSize: "0.75rem" }}>
                {resource.role}
              </div>
            </div>
          </div>
        </td>

        <td className="text-center">
          {hasWorkPackages ? (
            <div className="d-flex flex-column align-items-center justify-content-center">
              <Badge 
                bg="transparent" 
                className="border border-secondary txt-main px-2 py-1 opacity-75" 
                style={{ cursor: "default" }}
              >
                <span className="fw-bold">{resource.allocated?.toFixed(2)}</span>
                <span className="ms-1 fw-normal txt-subtle" style={{ fontSize: "0.7rem" }}>
                  {t('components.planningTab.table.total_eti_badge')}
                </span>
              </Badge>
              <span className="txt-subtle mt-1 fw-semibold" style={{ fontSize: "0.65rem" }}>
                {userWPs.length === 1 
                  ? t('components.planningTab.table.wp_count', { count: userWPs.length }) 
                  : t('components.planningTab.table.wp_count_plural', { count: userWPs.length })
                }
              </span>
            </div>
          ) : isEditing ? (
            <div className="d-flex align-items-center justify-content-center gap-1">
              <Form.Control
                type="number"
                step="0.05"
                min="0"
                max="1"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                style={{ width: "70px", fontSize: "0.8rem" }}
                size="sm"
                disabled={isSaving}
                autoFocus
              />
              {isSaving ? (
                 <Spinner animation="border" size="sm" className="mx-1" style={{ width: "12px", height: "12px", color: "var(--brand-primary)" }} />
              ) : (
                <>
                  <FaCheck role="button" className="text-success" onClick={handleSave} />
                  <FaTimes role="button" className="text-danger" onClick={handleCancel} />
                </>
              )}
            </div>
          ) : (
            <OverlayTrigger placement="top" overlay={<Tooltip>{t('components.planningTab.table.allocation_in_project', { projectName })}</Tooltip>}>
              <Badge 
                bg="white" 
                text={hasConflict ? "danger" : "dark"} 
                className="border px-2 py-1 d-inline-flex align-items-center gap-2 shadow-sm"
                role="button"
                onClick={() => setIsEditing(true)}
              >
                {resource.allocated?.toFixed(2)}
                <FaPen size={10} className="txt-subtle" />
              </Badge>
            </OverlayTrigger>
          )}
        </td>
        
        <td className={`text-end px-2 fw-semibold ${hasConflict ? "txt-danger" : "txt-subtle"}`} style={{ fontSize: "0.85rem" }}>
          <OverlayTrigger placement="top" overlay={<Tooltip>{t('components.planningTab.table.total_allocation_tooltip')}</Tooltip>}>
            <span>
              {hasConflict 
                ? t('components.planningTab.table.conflict_total', { eti: resource.total_eti?.toFixed(2) })
                : t('components.planningTab.table.alloc_total', { eti: resource.total_eti?.toFixed(2) })
              }
            </span>
          </OverlayTrigger>
        </td>

        <td className="text-center px-3" style={{ width: "40px" }}>
          <FaEllipsisV 
            role="button" 
            className="txt-subtle" 
            onClick={() => setShowModal(true)} 
            title={t('components.planningTab.table.manage_allocations')}
          />
        </td>
      </tr>

      <UserAllocationModal 
        show={showModal} 
        onHide={() => setShowModal(false)} 
        userId={resource.id}
        userName={getFullName(resource)}
        month={month}
      />
    </>
  );
};

export default ResourceRow;
